import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import warehouseCeoLogo from '../assets/warehouse_ceo_logo.jpg';
import { 
  Eye, 
  EyeOff, 
  Package, 
  ShoppingCart, 
  BarChart2, 
  Users, 
  ArrowLeftRight, 
  Brain, 
  Truck, 
  FileText, 
  Settings, 
  Zap, 
  User, 
  Lock, 
  Sparkles,
  ShieldCheck,
  Landmark,
  Calculator,
  Factory,
  Wrench,
  FolderKanban,
  Clock,
  Globe,
  Receipt
} from 'lucide-react';
import './Login.css';

const features = [
  { icon: Package, title: 'Inventory Management', desc: 'Real-time stock tracking, low-stock alerts, and multi-location warehouse control.' },
  { icon: ShoppingCart, title: 'Point of Sale (POS)', desc: 'Fast sales processing with barcode scanning, discounts, and multiple payment methods.' },
  { icon: Truck, title: 'Purchase & Receiving', desc: 'Manage supplier orders, goods receipts, and purchase returns seamlessly.' },
  { icon: Brain, title: 'AI Invoice & OCR Extraction', desc: 'Google Gemini AI invoice reader, auto SKU matching, and fallback OCR.', ai: true },
  { icon: Calculator, title: 'Double-Entry Accounting', desc: 'General Ledger, Chart of Accounts, Journal entries, Balance Sheet, and P&L reports.' },
  { icon: Landmark, title: 'Plaid Bank Integration', desc: 'Automated bank feed synchronization and real-time account reconciliation.' },
  { icon: Zap, title: 'QuickBooks Integration', desc: 'Sync sales, purchases, payments, and expenses directly with QuickBooks for accounting.' },
  { icon: FileText, title: 'Invoicing & EDI', desc: 'Auto-generate invoices, custom thermal templates, and EDI flat files.' },
  { icon: BarChart2, title: 'Reports & Analytics', desc: 'Profit & loss, stock reports, sales trends, and tax summaries at a glance.' },
  { icon: Users, title: 'Customer & Supplier CRM', desc: 'Manage contacts, credit limits, payment history, and loyalty programs.' },
  { icon: ArrowLeftRight, title: 'Stock Transfers & Adjustments', desc: 'Move products between warehouses and locations with full audit trail.' },
  { icon: Factory, title: 'Manufacturing & BOM', desc: 'Bill of Materials recipes, multi-stage production tracking, and scrap control.' },
  { icon: Wrench, title: 'Repair & Service Center', desc: 'Device repair job sheets, warranty status, serial tracking, and technician logs.' },
  { icon: FolderKanban, title: 'Project Management', desc: 'Task tracking, milestones, time logging, activity logs, and project billing.' },
  { icon: Clock, title: 'HRM, Payroll & Essentials', desc: 'Employee attendance, shift scheduling, leave management, and payroll slips.' },
  { icon: Globe, title: 'WooCommerce & Digital Catalog', desc: 'Bi-directional web store sync, order fulfillment, and public digital menu.' },
  { icon: Receipt, title: 'Expenses & Payment Accounts', desc: 'Track operating expenses, account balances, and payment methods.' },
  { icon: Settings, title: 'Role-Based Access & SaaS', desc: 'Granular permissions for staff, managers, admins, and SaaS multi-tenancy.' }
];

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setErrors({});
    try {
      const response = await login(formData.username, formData.password);
      if (response.success) {
        navigate('/');
      } else {
        setErrors({ general: response.message || 'Incorrect Username, Password, Or user not active.' });
      }
    } catch (error) {
      /* console.error('Login error:', error); */
      setErrors({ general: error.message || 'Incorrect Username, Password, Or user not active.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Panel - Login Form */}
      <div className="login-left-panel">
        <div className="login-left-content">
          {/* Product Logo & Parent Company Pill Badge */}
          <div className="login-logo">
            <img src={warehouseCeoLogo} alt="Warehouse CEO" className="login-product-logo" />
            <div className="login-logo-text-group">
              <span className="login-logo-text">Warehouse CEO</span>
            </div>
          </div>

          <div className="login-welcome">
            <h1>Welcome Back</h1>
            <p>Login to your Warehouse CEO portal</p>
          </div>

          {errors.general && (
            <div className="error-message">
              <span>{errors.general}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-with-icon">
                <User size={18} className="field-icon" />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`form-input ${errors.username ? 'error' : ''}`}
                  placeholder="Enter your username"
                  autoComplete="username"
                />
              </div>
              {errors.username && <span className="error-text">{errors.username}</span>}
            </div>

            <div className="form-group">
              <div className="password-label-row">
                <label htmlFor="password">Password</label>
                <a href="/forgot-password" className="forgot-password">Forgot Your Password?</a>
              </div>
              <div className="input-with-icon password-input-wrapper">
                <Lock size={18} className="field-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-remember">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <span>Remember Me</span>
              </label>
            </div>

            <button type="submit" className="login-btn" disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="btn-loading">
                  <span className="spinner"></span> Logging in...
                </span>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="login-footer">
            <div className="secure-badge">
              <ShieldCheck size={14} /> Encrypted &amp; Secure Session
            </div>
            <p>© 2026 Warehouse CEO. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Feature Showcase */}
      <div className="login-right-panel">
        <div className="login-ambient-orb orb-1"></div>
        <div className="login-ambient-orb orb-2"></div>
        <div className="login-ambient-orb orb-3"></div>

        <div className="login-right-content">
          <div className="login-hero-header">
            <div className="hero-pill">
              <Sparkles size={14} className="hero-pill-icon" />
              <span>Warehouse CEO</span>
            </div>
            <h2>Your Complete Warehouse<br /><span className="gradient-text">&amp; POS Solution</span></h2>
            <p>Everything you need to run your warehouse business smarter, faster, and seamlessly.</p>
          </div>

          <div className="login-features-grid">
            {features.map((feature, idx) => (
              <div className={`login-feature-card ${feature.ai ? 'ai-card' : ''}`} key={idx}>
                <div className="login-feature-icon">
                  <feature.icon size={18} />
                </div>
                <div className="login-feature-info">
                  <h4>
                    {feature.title}
                    {feature.ai && <span className="ai-badge-inline"><Sparkles size={9} /> AI</span>}
                  </h4>
                  <p>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
